/**
 * loopBBetaAccess — fail-closed Sichtbarkeits-Logik für den Loop-B-Kanzlei-Kanal
 * (WL-K0c, build-plan-loop-b.md §A.4).
 *
 * Single source of truth dafür, OB Loop-B-Menüpunkte/Panels/Buttons für den
 * aktuellen Nutzer angezeigt werden. Muster analog `advisorInviteAccess.ts`, aber
 * mit einer ZWEITEN, denormalisierten Freischalt-Quelle: dem „ansteckenden"
 * `sellers/{uid}.loopBBeta`-Flag (§A.3 Contagion). Jede Identität im Tenant-Graphen
 * des Beta-Owners trägt am Ende ihr EIGENES Flag, deshalb gatet die FE
 * AUSSCHLIESSLICH auf `currentUser.uid` + eigenes `sellerData` — KEIN
 * scope-basierter Zweitpfad (`effectiveScopeUid`) nötig (§A.4, „Wichtig").
 *
 * Sicherheitshinweis: rein KOSMETISCHES Client-Gating (wie `canInviteAdvisor`/
 * `canUseInboundDatev`) — KEIN Zugriffsschutz. Die harte, fail-closed Sperre bleibt
 * server-seitig in `functions/src/access/loopBBetaAccess.ts`
 * (`isLoopBBetaServer`/`assertLoopBBetaScope`). Ein client-gefälschtes `loopBBeta`
 * hilft nichts: `loopBBeta` ist ein backend-only Feld — `saveSellerData` strippt es
 * (wie `isPremium`), und die Firestore-Rules verbieten den Client-Write (Feld in der
 * `sellers`-Protected-Field-Allowlist).
 *
 * @module loopBBetaAccess
 */

/**
 * Beta-Allowlist Loop-B — Seed-UIDs, die IMMER beta sind (Bootstrap der
 * Zusammenhangskomponente). `b3DRR1Cj63cRjdJg5VCEUpBL7hk1` = Test-Account
 * daniel_eich@outlook.com (identisch zu `ADVISOR_INVITE_BETA_UIDS` &
 * `INBOUND_DATEV_EXPORT_BETA_TENANT_UIDS`). HARTKODIERT für die kontrollierte
 * Beta-Erprobung; muss deckungsgleich mit `LOOP_B_BETA_SEED_UIDS` in
 * `functions/src/access/loopBBetaAccess.ts` bleiben.
 */
export const LOOP_B_BETA_SEED_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Entscheidet fail-closed, ob Loop-B-UI für den aktuellen Nutzer sichtbar ist.
 *
 * WICHTIG: gatet auf `currentUser.uid` (die eigene Identität) + das eigene
 * `sellerData` — NICHT auf `effectiveScopeUid`. Weil die Contagion jeder
 * Graph-Identität ihr eigenes Flag gibt, ist ein Scope-Zweitpfad unnötig (§A.4).
 * Rein kosmetisch; die harte Sperre bleibt server-seitig.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @param sellerData - das eigene `sellerData` aus `useAuth()` (oder null/undefined,
 *                     solange noch nicht geladen). Nur das `loopBBeta`-Feld wird
 *                     gelesen.
 * @returns `true`, wenn `uid` auf der Seed-Allowlist steht ODER
 *          `sellerData.loopBBeta === true`. Sonst `false` (fail-closed — leeres
 *          oder fehlendes `sellerData`/`uid` ⇒ kein Zugriff).
 */
export function isLoopBBeta(
  uid: string | null | undefined,
  sellerData: { loopBBeta?: boolean } | null | undefined,
): boolean {
  if (!!uid && LOOP_B_BETA_SEED_UIDS.includes(uid)) {
    return true;
  }
  return sellerData?.loopBBeta === true;
}
